import { Plus, XCircle } from '@tamagui/lucide-icons';
import { FloatingButton } from '@unistocks/components/button/floating-action-button';
import { Group } from '@unistocks/components/group/group';
import { Sheet } from '@unistocks/components/sheet/sheet';
import { Heading } from '@unistocks/components/text/text';
import { t } from '@unistocks/localization';
import { useBackHandler } from '@unistocks/shared/hooks/use-backhandler';
import { useToggle } from '@unistocks/shared/hooks/use-toggle';
import { withFeedback } from '@unistocks/shared/utils/haptics/with-feedback';
import { SelectOption } from '@unistocks/types';
import { KeyValue } from '@unistocks/types/key-value';
import React, { Key, useCallback } from 'react';
import { getVariableValue, ListItem, Separator, useMedia, View } from 'tamagui';

export type FabMenuGroup<T = any> = KeyValue<Key, ReadonlyArray<SelectOption<T>>> & {
  groupHeader?: string;
};

export type FabMenuProps<T = any> = {
  items: ReadonlyArray<FabMenuGroup<T>>;
  onItemPress?: (value: T) => void;
  keyExtractor?: (item: SelectOption<T>) => string;
};

export const FabMenu: React.FC<FabMenuProps> = ({ items, onItemPress, keyExtractor }) => {
  const [open, toggleOpen, setOpen] = useToggle(false);
  const media = useMedia();
  const spacing5 = getVariableValue('$5', 'space');
  const spacing10 = getVariableValue('$10', 'space');

  useBackHandler(
    useCallback(() => {
      if (open) {
        setOpen(false);

        return true;
      }

      return false;
    }, [open, setOpen])
  );

  const renderItem = (item: SelectOption, index: number, arr: ReadonlyArray<SelectOption>) => (
    <Group.Item key={keyExtractor?.(item) ?? item.key}>
      <ListItem
        borderRadius={'$true'}
        size={'$5'}
        cursor="pointer"
        gap={'$3'}
        title={item.title}
        icon={item.icon}
        scaleIcon={1.5}
        onPressIn={withFeedback}
        onPress={() => {
          onItemPress?.(item.value);
          setOpen(false);
        }}
      />
    </Group.Item>
  );

  const renderGroup = (group: FabMenuGroup, index: number) => {
    return (
      <Group key={group.key} mt={index > 0 ? '$3' : 0} separator={<Separator />}>
        {group.groupHeader ? (
          <Group.Item>
            <View bg={'$color4'} px={'$5'} py={'$2'} borderRadius={'$true'}>
              <Heading size={'$6'} color={'$bodyFont'}>
                {group.groupHeader}
              </Heading>
            </View>
          </Group.Item>
        ) : null}

        {group.value.map(renderItem)}
      </Group>
    );
  };

  return (
    <>
      <FloatingButton Icon={Plus} onPress={toggleOpen} open={open} />
      <Sheet
        open={open}
        onOpenChange={setOpen}
        enableDynamicSizing
        enableContentPanningGesture={false}
        handleComponent={null}
        backgroundComponent={null}
        style={{ borderWidth: 0, marginHorizontal: media.sm ? spacing10 : spacing5 }}
        scrollViewProps={{ fadingEdgeLength: 0 }}
      >
        {items.map(renderGroup)}
        <Group size={'$5'} mt={'$3'}>
          <Group.Item>
            <ListItem
              size={'$5'}
              cursor="pointer"
              gap={'$3'}
              borderRadius={'$true'}
              scaleIcon={1.5}
              overflow="visible"
              title={t('AbpUi::Close')}
              icon={XCircle}
              onPressIn={withFeedback}
              onPress={() => setOpen(false)}
            />
          </Group.Item>
        </Group>
      </Sheet>
    </>
  );
};

FabMenu.displayName = 'FabMenu';
